<template>
	<div class="login">
		<Modal modal-size="md" :is-modal-open="true" :unclosable="true">
			<!-- Modal title / header -->
			<div class="modal__content__header header">
				<!-- The avana wholesale logo -->
				<AvanaLogo />
			</div>

			<!-- Login Form -->
			<form v-if="!showResetPassword" class="brand-form" @submit.prevent="login">
				<div class="form-container">
					<div class="form-value">
						<!-- Email input -->
						<label id="email-label" for="email">Email</label>
						<input required="required" :disabled="isLoading" type="email" v-model="email" id="email" name="email" />

						<!-- Password input -->
						<label id="password-label" for="password">Password</label>
						<input
							required="required"
							:disabled="isLoading"
							type="password"
							v-model="password"
							id="password"
							name="password"
							style="margin-bottom:0;"
						/>

						<!-- Show reset password view -->
						<a href="#" @click.prevent="showResetPassword = true" class="button-actions__cancel input-sub">
							<span>Forgot Your Password?</span>
						</a>
					</div>
				</div>

				<!-- Show an error message if there is one -->
				<p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

				<!-- Login buttons -->
				<div class="button-actions">
					<button
						data-cy="button-login"
						type="submit"
						:disabled="isLoading"
						class="button__login"
						:class="{
							'button--success': !isLoading,
							'button--submitting': isLoading
						}"
					>
						<span>Log In</span>
					</button>

					<!-- Go back to the home page -->
					<router-link :to="{ name: 'home' }" class="button__cancel">
						<span>Cancel</span>
					</router-link>
				</div>
			</form>

			<!-- Reset Password Form -->
			<form v-if="showResetPassword && !resetMessage" class="brand-form" @submit.prevent="resetPassword">
				<div class="form-container">
					<h2 class="header__subtitle" style="padding-bottom:30px;">Forgot Your Password?</h2>

					<div class="form-value">
						<!-- Reset password email input -->
						<label id="reset-email-label" for="reset-email">Enter your email to reset your password.</label>
						<input
							required="required"
							:disabled="isLoading"
							type="email"
							v-model="resetEmail"
							id="reset-email"
							name="reset-email"
							style="margin-bottom:0;"
						/>
					</div>
				</div>

				<!-- Show an error message if there is one -->
				<p v-if="resetErrorMessage" class="error-message">{{ resetErrorMessage }}</p>

				<!-- Login buttons -->
				<div class="button-actions">
					<button
						type="submit"
						:disabled="isLoading"
						class="button__login"
						:class="{
							'button--success': !isLoading,
							'button--submitting': isLoading
						}"
					>
						<span>Reset Password</span>
					</button>

					<!-- Go back to login view -->
					<a href="#" @click.prevent="showResetPassword = false" class="button__cancel">
						<span>Go Back</span>
					</a>
				</div>
			</form>

			<!-- Reset password success view -->
			<div v-if="showResetPassword && resetMessage" class="reset-message">
				<span>{{ resetMessage }}</span>
				<!-- Go back to the home page -->
				<div class="button-actions">
					<router-link :to="{ name: 'home' }" class="button__cancel">
						<span>Return to the Homepage</span>
					</router-link>
				</div>
			</div>
		</Modal>
	</div>
</template>

<script>
import AvanaLogo from '@/components/icons/AvanaLogo';
import CREATE_PASSWORD_RESET from '@/graphql/mutations/createPasswordReset.gql';
// Common error codes
import { UNAUTHENTICATED } from '@/utilities/errorCodes';
import logger from 'client-logger';
import { HAPPENS_AGAIN } from '@/utilities/errorResponses';

// For some reason I cannot fathom, Vue's data reactivity is not working with these two variables!!!
let permissionsLoaded = false;
let loggedIn = false;
export default {
	data() {
		return {
			email: '',
			password: '',
			isLoading: false,
			errorMessage: '',
			showResetPassword: false,
			resetEmail: '',
			resetMessage: '',
			resetErrorMessage: ''
		};
	},
	methods: {
		async login() {
			// Disable the login
			this.isLoading = true;

			const error = await this.$auth.login(this.email, this.password);

			if (error) {
				if (error.code === UNAUTHENTICATED) {
					// Show message
					this.errorMessage = 'Incorrect email or password';
				} else {
					// Show general error message
					this.errorMessage = `We seem to be having difficulty logging you in, please try again later. ${HAPPENS_AGAIN}`;
				}

				this.isLoading = false;
			}
		},
		handleLoginEvent(data) {
			loggedIn = data.loggedIn;

			// Redirect if ready
			this.redirect();
		},
		redirect() {
			if (loggedIn && permissionsLoaded) {
				// Now that they've logged in and we have permissions, send them to where they originally wanted to go
				const target = this.$route.query.redirect || '/';
				this.$router.replace({ path: target });
			}
		},
		async resetPassword() {
			// Disable the submit button
			this.isLoading = true;

			// Define the mutation
			const mutation = {
				mutation: CREATE_PASSWORD_RESET,
				variables: {
					email: this.resetEmail
				}
			};

			// Send the mutation
			try {
				await this.$apollo.mutate(mutation);

				this.resetMessage =
					'Success! If we have an account on file with the email address you have submitted, you will receive an email from us in your inbox. Please follow the instructions in the next 12 hours to reset your password.';
			} catch (e) {
				logger.error({ error: e }, 'Failed to create password reset');

				// Show an error message
				this.resetErrorMessage = `We seem to be having difficulty processing your password reset request. ${HAPPENS_AGAIN}`;
			}

			this.isLoading = false;
		}
	},
	components: {
		AvanaLogo,
		Modal: () => import(/* webpackChunkName: "Modal" */ '@/components/shared/Modal')
	},
	watch: {
		'$store.Role.allPermissionsByRole': {
			immediate: true,
			handler(newValue) {
				// Check to see if we have permissions loaded yet
				if (Object.entries(newValue).length > 0) {
					permissionsLoaded = true;

					// Redirect if ready
					this.redirect();
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/brand-container.scss';
@import '@/styles/mixins/brand-fonts.scss';
@import '@/styles/mixins/brand-form.scss';
@import '@/styles/mixins/brand-button.scss';
@import '@/styles/mixins/brand-link.scss';

.avana-logo {
	font-size: 19px !important;
}
.header {
	&__subtitle {
		font-family: $brand-font-pro-bold;
		font-size: 36px;
		margin-top: 24px;
	}
}

.error-message {
	text-align: center;
	max-width: 350px;
	margin: 10px auto 40px;
	@include Brand-P2();
	color: $brand-danger;
}

.reset-message {
	text-align: center;
	width: 80%;
	@include Brand-P2();
	margin: auto auto;
}

.button-actions {
	display: flex;
	flex-direction: column;
	margin-top: 36px;

	.button {
		&__login {
			@include Brand-Button(primary, lg);
			max-width: 350px;
			margin-bottom: 30px;
		}
		&__cancel {
			@include Brand-Link($brand-primary);
		}
	}

	&__cancel.input-sub {
		padding-left: 0;

		&:before {
			left: 0;
		}
	}
}
</style>
